// The revenue-report currency picker (SM-495) — "whose fee revenue am I
// looking at", and why this one is REQUIRED where the positions filter is
// optional.
//
// Treasury → Revenue reports an AGGREGATE: totals, day buckets, fill counts, a
// maker/taker/redeem split. An "All currencies" option would have to add RED,
// GREEN and GOLD minor units together, and that sum is not a number — the three
// share a wire scale but not a value (G8). So there is no such option, the
// control always names exactly one currency, and every figure on the page is
// labelled with it.
//
// GOLD is the default because it is what the page has always reported: every
// pre-SM-432 fee_charges row carries no currency at all and reads as GOLD, and
// an operator who has never touched this control must keep seeing the number
// they saw yesterday.
//
// All three codes are offered, unlike <StakeCurrencyFilter> which excludes
// GREEN. That is not an inconsistency: nothing is ever STAKED in GREEN, but fee
// revenue is genuinely COLLECTED in it — a GAME sell withholds its fee from the
// GREEN proceeds — so a GREEN option here answers a real question.

import { Select } from "@/components/ui/Select";
import { CurrencyMonogram } from "@/components/ui/CurrencyAmount";
import { useCurrencySettings } from "@/components/currency/CurrencyPresentationProvider";
import { useRegistryOrderedCurrencies } from "@/components/currency/registryOrder";
import { CURRENCY_CODES, isCurrencyCode, type CurrencyCode } from "@/lib/currency";
import { resolveCurrencyPresentation } from "@/currency";

/**
 * The currency a revenue report falls back to when nothing selects one.
 *
 * Same constant as settlement-service's `ports.RevenueAggregateCurrency`, and
 * for the same reason — see the note above.
 */
export const DEFAULT_REVENUE_CURRENCY: CurrencyCode = "GOLD";

/**
 * Read a revenue currency out of untrusted text (a URL search param).
 *
 * Undefined for anything unrecognised, which the page turns into the GOLD
 * default. Left as undefined rather than coerced here so the caller can tell a
 * URL that asked for nothing from one that asked for something impossible; the
 * server keeps its own fail-closed guard and refuses an unknown code outright,
 * rather than serving GOLD's revenue under another currency's label.
 */
export function parseRevenueCurrency(raw: unknown): CurrencyCode | undefined {
  return typeof raw === "string" && isCurrencyCode(raw) ? raw : undefined;
}

/**
 * The picker. Options carry each currency's CONFIGURED name and mark — whatever
 * an operator set on /currencies — so the control reads the same as the figures
 * it governs rather than as three wire codes.
 */
export function RevenueCurrencyFilter({
  value,
  onChange,
  label = "Currency",
  className,
}: {
  value: CurrencyCode;
  onChange: (next: CurrencyCode) => void;
  label?: string;
  className?: string;
}) {
  const settings = useCurrencySettings();
  const options = useRegistryOrderedCurrencies(CURRENCY_CODES);
  return (
    <Select
      className={className}
      label={label}
      value={value}
      onChange={(e) => onChange(parseRevenueCurrency(e.target.value) ?? DEFAULT_REVENUE_CURRENCY)}
      options={options.map((code) => ({
        value: code,
        // Name AND code: the name is what the console shows everywhere else,
        // the code is what a support ticket or an API call says.
        label: `${resolveCurrencyPresentation(code, settings).displayName} (${code})`,
        icon: <CurrencyMonogram currency={code} />,
      }))}
    />
  );
}
